// HUB-UI: shared loading, errors, entity grids and edit modals for every new Hub page.
import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { SvgIconComponent } from '@mui/icons-material';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import Refresh from '@mui/icons-material/Refresh';
import InboxOutlined from '@mui/icons-material/InboxOutlined';
import RestaurantOutlined from '@mui/icons-material/RestaurantOutlined';
import HotelOutlined from '@mui/icons-material/HotelOutlined';
import MedicationOutlined from '@mui/icons-material/MedicationOutlined';
import BuildOutlined from '@mui/icons-material/BuildOutlined';
import SpaOutlined from '@mui/icons-material/SpaOutlined';
import HubOutlined from '@mui/icons-material/HubOutlined';
import AppsOutlined from '@mui/icons-material/AppsOutlined';
import HomeWorkOutlined from '@mui/icons-material/HomeWorkOutlined';
import LocalGroceryStoreOutlined from '@mui/icons-material/LocalGroceryStoreOutlined';
import ConstructionOutlined from '@mui/icons-material/ConstructionOutlined';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import type { Values } from './hubApi';
import { useAppSelector } from '../../app/store';

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'The request could not be completed. Please try again.';
}
export function useHubLoad<T>(load: (signal: AbortSignal) => Promise<T>, retainDataOnReload = false) {
  // Switching companies invalidates loaded rows; never retain another tenant's grid.
  const organizationId = useAppSelector((state) => state.session.activeOrganizationId);
  const userId = useAppSelector((state) => state.session.profile.id);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    setData(null);
    setError(null);
  }, [load, organizationId, userId]);
  useEffect(() => {
    const controller = new AbortController();
    if (!retainDataOnReload) setData(null);
    setError(null);
    load(controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setData(value);
      })
      .catch((caught) => {
        if (!controller.signal.aborted) setError(errorMessage(caught));
      });
    return () => controller.abort();
  }, [load, attempt, organizationId, userId, retainDataOnReload]);
  const reload = useCallback(() => setAttempt((value) => value + 1), []);
  return { data, error, reload };
}
export function LoadState({ data, error, reload }: { data: unknown; error: string | null; reload: () => void }) {
  const { t } = useTranslation();
  if (error)
    return (
      <div className="hub-state is-error" role="alert">
        <span className="hub-state-icon" aria-hidden>
          <ErrorOutline />
        </span>
        <span className="hub-state-text">{error}</span>
        <Button variant="contained" disableElevation startIcon={<Refresh />} onClick={reload}>
          {t('hub.retry', 'Retry')}
        </Button>
      </div>
    );
  return data === null ? (
    <div className="hub-loading" role="status">
      <CircularProgress size={20} thickness={5} aria-hidden />
      <span>{t('hub.loading', 'Loading')}…</span>
    </div>
  ) : null;
}
/** Soft empty state: icon, one short line, optional action. */
export function HubEmpty({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="hub-state">
      <span className="hub-state-icon" aria-hidden>
        <InboxOutlined />
      </span>
      <span className="hub-state-text">{children}</span>
      {action}
    </div>
  );
}

/* ---- dashboard primitives ---- */

/** Product codes map to one icon and one fixed hue, mixed into the theme surface in CSS. */
export const PRODUCT_MARKS: Record<string, { icon: SvgIconComponent; hue: string }> = {
  PLATFORM: { icon: HubOutlined, hue: 'var(--primary, #2563eb)' },
  RESTAURANT: { icon: RestaurantOutlined, hue: '#ea580c' },
  HOTEL: { icon: HotelOutlined, hue: '#16a34a' },
  PHARMACY: { icon: MedicationOutlined, hue: '#0891b2' },
  AUTOSERVICE: { icon: BuildOutlined, hue: '#ca8a04' },
  COSMETICS: { icon: SpaOutlined, hue: '#c026d3' },
  REALESTATE: { icon: HomeWorkOutlined, hue: '#7c3aed' },
  SUPERMARKET: { icon: LocalGroceryStoreOutlined, hue: '#65a30d' },
  CONSTRUCTION: { icon: ConstructionOutlined, hue: '#d97706' },
  FINANCE: { icon: AccountBalanceOutlined, hue: '#0f766e' },
};
export function productMark(code: string) {
  return PRODUCT_MARKS[code.toUpperCase()] ?? { icon: AppsOutlined, hue: 'var(--primary, #2563eb)' };
}
/** Rounded product icon tile; `size` is the tile edge in px. */
export function ProductMark({ code, size = 44 }: { code: string; size?: number }) {
  const { icon: Icon, hue } = productMark(code);
  return (
    <span className="hub-mark" style={{ '--mark': hue, width: size, height: size } as CSSProperties} aria-hidden>
      <Icon sx={{ fontSize: Math.round(size * 0.5) }} />
    </span>
  );
}

/**
 * Bars drawn from real values only (e.g. apps per company). Each bar gets a title for pointer
 * users; the figure itself is decorative next to the number it explains.
 */
export function MiniBars({ values, labels }: { values: number[]; labels?: string[] }) {
  if (values.length === 0) return null;
  const max = Math.max(1, ...values);
  const shown = values.slice(0, 16);
  const w = 120;
  const h = 40;
  const gap = 4;
  const bw = Math.max(4, (w - gap * (shown.length - 1)) / shown.length);
  return (
    <svg className="hub-minibars" viewBox={`0 0 ${w} ${h}`} width={w} height={h} aria-hidden="true">
      {shown.map((value, index) => {
        const bh = Math.max(4, (value / max) * h);
        return (
          <rect key={index} x={index * (bw + gap)} y={h - bh} width={bw} height={bh} rx={Math.min(4, bw / 2)} style={{ animationDelay: `${index * 40}ms` }}>
            {labels?.[index] && <title>{`${labels[index]}: ${value}`}</title>}
          </rect>
        );
      })}
    </svg>
  );
}

/** KPI tile: icon, large number, short label, optional real-data figure. */
export function HubStat({
  icon: Icon,
  label,
  value,
  to,
  figure,
}: {
  icon: SvgIconComponent;
  label: string;
  value: ReactNode;
  to?: string;
  figure?: ReactNode;
}) {
  const body = (
    <>
      <span className="hub-stat-icon" aria-hidden>
        <Icon />
      </span>
      <strong className="hub-stat-value">{value}</strong>
      <span className="hub-stat-label">{label}</span>
      {figure && <span className="hub-stat-figure">{figure}</span>}
    </>
  );
  return to ? (
    <Link className="hub-stat is-link" to={to}>
      {body}
    </Link>
  ) : (
    <div className="hub-stat">{body}</div>
  );
}

export function HubTitle({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <header className="hub-page-head">
      <div>
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action && <div className="hub-page-actions">{action}</div>}
    </header>
  );
}
/** A titled block inside a hub page; keeps heading levels and spacing consistent. */
export function HubSection({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="hub-block">
      <div className="hub-block-head">
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}
/** Previous / next paging for server-paged grids. */
export function HubPager({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1 && page === 1) return null;
  return (
    <nav className="hub-pager" aria-label={t('hub.search.pages', 'Pages')}>
      <Button size="small" disabled={page === 1} onClick={() => onPage(page - 1)}>
        {t('hub.search.previous', 'Previous')}
      </Button>
      <span aria-live="polite">{t('hub.search.pageOf', 'Page {{page}} of {{pages}}', { page, pages })}</span>
      <Button size="small" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        {t('hub.search.next', 'Next')}
      </Button>
    </nav>
  );
}
export interface Field {
  key: string;
  label: string;
  type?: 'text' | 'email' | 'url' | 'date' | 'number' | 'boolean' | 'select' | 'multiline';
  required?: boolean;
  maxLength?: number;
  min?: number;
  max?: number;
  options?: { value: string; label: string }[];
  helper?: string;
  readOnly?: boolean;
}
// Field specs deliberately contain editable business fields only: no tenant/user IDs or audit columns.
export function Fields({
  fields,
  value,
  onChange,
  disabled = false,
}: {
  fields: Field[];
  value: Values;
  onChange: (value: Values) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Box className="hub-form-grid">
      {fields.map((field) =>
        field.type === 'boolean' ? (
          <FormControlLabel
            key={field.key}
            label={field.label}
            control={
              <Switch
                checked={value[field.key] === true}
                disabled={disabled}
                onChange={(event) => onChange({ ...value, [field.key]: event.target.checked })}
              />
            }
          />
        ) : (
          <TextField
            key={field.key}
            label={field.label}
            value={value[field.key] ?? ''}
            required={field.required}
            disabled={disabled}
            fullWidth
            type={field.type === 'multiline' || field.type === 'select' ? 'text' : (field.type ?? 'text')}
            multiline={field.type === 'multiline'}
            minRows={field.type === 'multiline' ? 3 : undefined}
            select={field.type === 'select'}
            helperText={field.helper}
            InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
            inputProps={{
              readOnly: field.readOnly,
              maxLength: field.maxLength,
              min: field.min,
              max: field.max,
              step: field.type === 'number' ? 'any' : undefined,
            }}
            onChange={(event) =>
              onChange({
                ...value,
                [field.key]:
                  field.type === 'number'
                    ? event.target.value === ''
                      ? null
                      : Number(event.target.value)
                    : event.target.value,
              })
            }
          >
            {field.type === 'select' && [
              <MenuItem value="" key="empty">
                {t('hub.choose', 'Choose…')}
              </MenuItem>,
              ...(field.options ?? []).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              )),
            ]}
          </TextField>
        ),
      )}
    </Box>
  );
}
export function EntityDialog({
  title,
  value,
  fields,
  save,
  close,
  validate,
  renderExtra,
  renderFields,
}: {
  title: string;
  value: Values;
  fields: Field[];
  save: (values: Values) => Promise<void>;
  close: () => void;
  validate?: (values: Values) => string | null;
  renderExtra?: (values: Values, onChange: (values: Values) => void, disabled: boolean) => ReactNode;
  renderFields?: (values: Values, onChange: (values: Values) => void, disabled: boolean) => ReactNode;
}) {
  const [form, setForm] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { t } = useTranslation();
  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={() => {
        if (!busy) close();
      }}
      aria-labelledby="hub-edit-title"
    >
      <Box
        component="form"
        onSubmit={async (event) => {
          event.preventDefault();
          const invalid = validate?.(form);
          if (invalid) {
            setError(invalid);
            return;
          }
          setBusy(true);
          setError(null);
          try {
            await save(form);
            close();
          } catch (caught) {
            setError(errorMessage(caught));
          } finally {
            setBusy(false);
          }
        }}
      >
        <DialogTitle id="hub-edit-title">{title}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ pt: 1 }}>
            {renderFields ? renderFields(form, setForm, busy) : <Fields fields={fields} value={form} onChange={setForm} disabled={busy} />}
            {renderExtra?.(form, setForm, busy)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={close}>
            {t('hub.cancel', 'Cancel')}
          </Button>
          <Button type="submit" variant="contained" disabled={busy}>
            {busy ? t('hub.saving', 'Saving…') : t('hub.save', 'Save')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
export function EntityGrid<T>({
  rows,
  columns,
  rowKey,
  actions,
  empty,
}: {
  rows: T[];
  columns: { label: string; render: (row: T) => ReactNode }[];
  rowKey: (row: T) => string;
  actions?: (row: T) => ReactNode;
  empty?: string;
}) {
  const { t } = useTranslation();
  const actionsLabel = t('hub.actions', 'Actions');
  return (
    <TableContainer className="hub-entity-grid">
      <Table>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.label}>{column.label}</TableCell>
            ))}
            {actions && <TableCell>{actionsLabel}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={rowKey(row)}>
              {columns.map((column) => (
                <TableCell key={column.label} data-label={column.label}>
                  {column.render(row) ?? '—'}
                </TableCell>
              ))}
              {actions && (
                <TableCell className="hub-grid-actions">
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {actions(row)}
                  </Stack>
                </TableCell>
              )}
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell className="hub-grid-empty" colSpan={columns.length + (actions ? 1 : 0)}>
                {empty ?? t('hub.noRecords', 'No records yet.')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
